/* eslint-disable max-lines */
import { PDFDocument, PDFFont, PDFPage, StandardFonts, rgb } from 'pdf-lib';

export type PdfTemplateFontWeight = 'regular' | 'bold';

export type PdfTemplateLayoutItem =
  | {
      type: 'text';
      text: string;
      x: number;
      y?: number;
      page?: number;
      fontSize?: number;
      fontWeight?: PdfTemplateFontWeight;
      maxWidth?: number;
      lineHeight?: number;
      spacingAfter?: number;
      textColor?: string;
    }
  | {
      type: 'field';
      label: string;
      value: string;
      x: number;
      y?: number;
      page?: number;
      labelFontSize?: number;
      valueFontSize?: number;
      maxWidth?: number;
      lineHeight?: number;
      spacingAfter?: number;
    }
  | {
      type: 'page-break';
      pageSize?: [number, number];
      cursorY?: number;
    }
  | {
      type: 'hr';
      x?: number;
      thickness?: number;
      color?: string;
      spacingAfter?: number;
      y?: number;
    }
  | {
      type: 'spacing';
      height: number;
    };

export interface GeneratePdfTemplateOptions {
  templatePath: string;
  layout: PdfTemplateLayoutItem[];
  cursorStart?: number;
  bottomMargin?: number;
  spacing?: number;
  templateLastPageIndex?: number;
}

export interface GeneratePdfTemplateResult {
  blob: Blob;
  url: string;
  pdfBytes: Uint8Array;
}

const DEFAULT_LINE_HEIGHT = 14;
const DEFAULT_SPACING = 12;
const DEFAULT_BOTTOM_MARGIN = 72;

const normalizeTextValue = (value: string) => (value ?? '').toString();

const resolveColor = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const hex = value.trim().replace(/^#/, '');
  if (hex.length !== 3 && hex.length !== 6) {
    return undefined;
  }

  const expand =
    hex.length === 3
      ? hex
          .split('')
          .map((char) => `${char}${char}`)
          .join('')
      : hex;
  const intValue = parseInt(expand, 16);
  if (Number.isNaN(intValue)) {
    return undefined;
  }

  const r = parseInt(expand.slice(0, 2), 16) / 255;
  const g = parseInt(expand.slice(2, 4), 16) / 255;
  const b = parseInt(expand.slice(4, 6), 16) / 255;

  return rgb(r, g, b);
};

const wrapParagraph = (paragraph: string, font: PDFFont, fontSize: number, maxWidth: number) => {
  if (!paragraph || !maxWidth || maxWidth <= 0) {
    return [paragraph];
  }

  const words = paragraph.split(/\s+/).filter((word) => word.length > 0);

  if (words.length === 0) {
    return [''];
  }

  const lines: string[] = [];
  let currentLine = '';

  const flushCurrentLine = () => {
    if (currentLine.trim().length === 0) {
      lines.push('');
    } else {
      lines.push(currentLine);
    }
    currentLine = '';
  };

  const splitLongWord = (word: string) => {
    let buffer = '';
    for (const char of word) {
      const tentative = buffer + char;
      const tentativeWidth = font.widthOfTextAtSize(tentative, fontSize);
      if (tentativeWidth <= maxWidth || buffer.length === 0) {
        buffer = tentative;
      } else {
        if (buffer.length > 0) {
          lines.push(buffer);
        }
        buffer = char;
      }
    }
    if (buffer.length > 0) {
      lines.push(buffer);
    }
  };

  for (const word of words) {
    const tentativeLine = currentLine.length > 0 ? `${currentLine} ${word}` : word;
    const width = font.widthOfTextAtSize(tentativeLine, fontSize);

    if (width <= maxWidth) {
      currentLine = tentativeLine;
      continue;
    }

    if (currentLine.length > 0) {
      flushCurrentLine();
    }

    const singleWordWidth = font.widthOfTextAtSize(word, fontSize);
    if (singleWordWidth <= maxWidth) {
      currentLine = word;
    } else {
      splitLongWord(word);
      currentLine = '';
    }
  }

  if (currentLine.length > 0) {
    flushCurrentLine();
  }

  return lines.length > 0 ? lines : [''];
};

const wrapText = (text: string, font: PDFFont, fontSize: number, maxWidth?: number) => {
  if (!maxWidth || maxWidth <= 0) {
    return normalizeTextValue(text).replace(/\r\n/g, '\n').split('\n');
  }

  const paragraphs = normalizeTextValue(text).replace(/\r\n/g, '\n').split('\n');
  const wrappedLines: string[] = [];

  for (const paragraph of paragraphs) {
    wrappedLines.push(...wrapParagraph(paragraph, font, fontSize, maxWidth));
  }

  return wrappedLines;
};

export const generatePdfFromTemplate = async ({
  templatePath,
  layout,
  cursorStart,
  bottomMargin,
  spacing,
  templateLastPageIndex,
}: GeneratePdfTemplateOptions): Promise<GeneratePdfTemplateResult> => {
  const response = await fetch(templatePath);

  if (!response.ok) {
    throw new Error(`Template not found: ${templatePath}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const fonts: Record<PdfTemplateFontWeight, PDFFont> = {
    regular: regularFont,
    bold: boldFont,
  };

  const defaultSpacing = spacing ?? DEFAULT_SPACING;
  const marginBottom = bottomMargin ?? DEFAULT_BOTTOM_MARGIN;

  const ensurePage = (
    pageIndex: number,
    pageSize?: [number, number],
  ): { page: PDFPage; height: number; width: number } => {
    const pages = pdfDoc.getPages();
    const basePage = pages[0];
    const baseWidth = pageSize ? pageSize[0] : basePage.getWidth();
    const baseHeight = pageSize ? pageSize[1] : basePage.getHeight();

    if (templateLastPageIndex !== undefined && pageIndex >= templateLastPageIndex && pages.length > pageIndex) {
      const newPage = pdfDoc.insertPage(pageIndex, [baseWidth, baseHeight]);
      return { page: newPage, height: baseHeight, width: baseWidth };
    }

    if (pageIndex < pages.length) {
      const page = pages[pageIndex];
      return { page, height: page.getHeight(), width: page.getWidth() };
    }

    const newPage = pdfDoc.addPage([baseWidth, baseHeight]);
    return { page: newPage, height: baseHeight, width: baseWidth };
  };

  let currentPageIndex = 0;
  let { page: currentPage, height: currentPageHeight, width: currentPageWidth } = ensurePage(currentPageIndex);
  let cursorY = cursorStart ?? currentPageHeight - 60;

  const moveToNextPage = (preferredCursor?: number, pageSize?: [number, number]) => {
    currentPageIndex += 1;
    const pageData = ensurePage(currentPageIndex, pageSize);
    currentPage = pageData.page;
    currentPageHeight = pageData.height;
    currentPageWidth = pageData.width;
    cursorY = preferredCursor ?? currentPageHeight - 60;
  };

  for (const item of layout) {
    if (item.type === 'page-break') {
      moveToNextPage(item.cursorY, item.pageSize);
      continue;
    }

    if (item.type === 'spacing') {
      cursorY -= item.height;
      if (cursorY <= marginBottom) {
        moveToNextPage();
      }
      continue;
    }

    const targetPageIndex = 'page' in item && item.page !== undefined ? item.page : currentPageIndex;
    if (targetPageIndex !== currentPageIndex) {
      const pageData = ensurePage(targetPageIndex);
      currentPageIndex = targetPageIndex;
      currentPage = pageData.page;
      currentPageHeight = pageData.height;
      currentPageWidth = pageData.width;
    }

    const itemX = 'x' in item ? item.x ?? 0 : 0;
    const maxWidth = 'maxWidth' in item && item.maxWidth ? item.maxWidth : currentPageWidth - itemX - 40;

    if (item.y !== undefined) {
      cursorY = item.y;
    } else if (cursorY <= marginBottom) {
      moveToNextPage();
    }

    if (item.type === 'hr') {
      const hrX = item.x ?? 54;
      const thickness = item.thickness ?? 1;
      const lineY = cursorY - thickness / 2;
      const endX = currentPageWidth - 40;
      const color = resolveColor(item.color) ?? rgb(0, 0, 0);

      currentPage.drawLine({
        start: { x: hrX, y: lineY },
        end: { x: endX, y: lineY },
        thickness,
        color,
      });

      cursorY = lineY - (item.spacingAfter ?? defaultSpacing);
      continue;
    }

    if (item.type === 'text') {
      const font = item.fontWeight === 'bold' ? fonts.bold : fonts.regular;
      const fontSize = item.fontSize ?? 12;
      const lineHeight = item.lineHeight ?? DEFAULT_LINE_HEIGHT;
      let drawY = item.y !== undefined ? item.y : cursorY;
      const wrappedLines = wrapText(item.text, font, fontSize, maxWidth);
      const color = resolveColor(item.textColor);

      for (let index = 0; index < wrappedLines.length; index++) {
        if (drawY < marginBottom) {
          moveToNextPage();
          drawY = cursorY;
        }
        currentPage.drawText(wrappedLines[index], {
          x: item.x,
          y: drawY,
          font,
          size: fontSize,
          color,
        });
        drawY -= lineHeight;
      }

      cursorY = drawY - (item.spacingAfter ?? defaultSpacing);
      continue;
    }

    if (item.type === 'field') {
      const labelFontSize = item.labelFontSize ?? 11;
      const valueFontSize = item.valueFontSize ?? 10;
      const lineHeight = item.lineHeight ?? DEFAULT_LINE_HEIGHT;
      const labelText = `${item.label}:`;
      let drawY = item.y !== undefined ? item.y : cursorY;

      if (drawY < marginBottom) {
        moveToNextPage();
        drawY = cursorY;
      }
      currentPage.drawText(labelText, {
        x: item.x,
        y: drawY,
        font: fonts.bold,
        size: labelFontSize,
      });
      drawY -= lineHeight;

      const valueText = normalizeTextValue(item.value);
      const valueLines = wrapText(valueText, fonts.regular, valueFontSize, maxWidth);

      for (let index = 0; index < valueLines.length; index++) {
        if (drawY < marginBottom) {
          moveToNextPage();
          drawY = cursorY;
        }
        currentPage.drawText(valueLines[index], {
          x: item.x,
          y: drawY,
          font: fonts.regular,
          size: valueFontSize,
        });
        drawY -= lineHeight;
      }

      cursorY = drawY - (item.spacingAfter ?? defaultSpacing);
    }
  }

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  return {
    blob,
    url,
    pdfBytes,
  };
};
